import find from 'lodash/find'
import { createSerializer, useQueryStates } from 'nuqs'
import { useMemo } from 'react'

import { createQueryStatesKeys, processURLSearchParams } from '@Globals/Algolia/Shared'
import { canonicalParts } from '@Globals/Page/CanonicalParts'
import { PageProps } from '@Globals/Page/Types'
import { useTranslation } from '@Globals/Translations'

export interface PageCanonical {
  titleAfter: string[]
  titleBefore: string[]
  /** The canonical URL: the CMS url plus only the query parameters that have a canonical meaning. */
  url: string
  /** Query string (sorted, with the leading `?`) appended to the CMS url; empty when nothing canonical is in the query. */
  urlExtra: string
}

const NO_FACETS: never[] = []

/**
 * The page's canonical URL and the title parts that go with it (canonicalParts), read and rendered through the
 * pagination block's own nuqs key map, so `page` and `filter_*` are parsed exactly as the block parses them.
 */
export const usePageCanonical = (
  page: { url?: string } | undefined,
  blockConfig: PageProps['blockConfig'] | undefined,
): PageCanonical => {
  const { t } = useTranslation()
  const algolia = find(blockConfig, block => block.algolia?.usePagination === true)?.algolia
  const keyMap = useMemo(() => createQueryStatesKeys(algolia?.facetParameters ?? NO_FACETS), [algolia?.facetParameters])
  const [states] = useQueryStates(keyMap)
  const serialize = useMemo(() => createSerializer(keyMap, { processUrlSearchParams: processURLSearchParams }), [keyMap])

  return useMemo(() => {
    const pageURL = page?.url || ''
    const canonicals = algolia?.canonicals
    if (!canonicals) {
      return { url: pageURL, urlExtra: '', titleAfter: [], titleBefore: [] }
    }

    const { canonical, titleAfter, titleBefore } = canonicalParts(states, canonicals, t)
    const urlExtra = serialize(canonical)
    return { url: `${pageURL}${urlExtra}`, urlExtra, titleAfter, titleBefore }
  }, [algolia?.canonicals, page?.url, serialize, states, t])
}
